import React, { useEffect } from "react";
import { reduxForm, Field } from "redux-form";
import { useDispatch, useSelector } from "react-redux";
import { StepButtons, CustomInput } from "components";
import styles from "../stepper.module.scss";
import { Row, Col } from "react-bootstrap";
import {
	validateServicesProductsSold,
	validateFederalTaxID,
	validateBusinessStartDate,
	validateAvgSaleAmount,
	validatePhysicalCardNotPresent,
	validateHighestSingleSaleAmount,
	validateMonthlyCreditCardVolume,
	validatePhysicalCardPresent,
} from "./form";
import {
	normalizeTaxId,
	normalizeFloat,
	onlyDate,
	positive,
	onlyPercent,
	trim,
	noSpace,
	normalizeAll,
	trimMidSentence,
	briefDescriptionNormalizer,
} from "validators";
import { servicePageActions, uiActions } from "store";
import { getStringWithoutHyphens } from "helpers";

const FORM_NAME = "service";

const ServiceStep = ({
	prevStep,
	nextStep,
	handleSubmit,
	submitting,
	token,
}) => {
	const dispatch = useDispatch();
	useEffect(() => {
		if (submitting) {
			dispatch(uiActions.showSpinnerOverlay());
		}
		return () => dispatch(uiActions.hideSpinnerOverlay());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [submitting]);

	const applicationId = useSelector(state => state.generalInfo.applicationId);

	const saveService = values => {
		const mappedValues = {
			...values,
			taxId: getStringWithoutHyphens(values?.taxId),
		};
		return dispatch(
			servicePageActions.sendService({
				...mappedValues,
				token,
				applicationId,
			}),
		).then(nextStep);
	};

	return (
		<form onSubmit={handleSubmit(saveService)} className={styles.form}>
			<Row>
				<Col lg={6} md={12}>
					<Field
						component={CustomInput}
						name="serviceProductsSold"
						label="Services / Products Sold"
						validate={validateServicesProductsSold}
						normalizeOnBlur={trim}
						normalize={normalizeAll([
							trimMidSentence,
							briefDescriptionNormalizer,
						])}
						tabIndex={1}
					/>
					<Field
						component={CustomInput}
						name="taxId"
						label="Federal Tax ID"
						validate={validateFederalTaxID}
						normalize={normalizeTaxId}
						tabIndex={3}
					/>
				</Col>
				<Col lg={6} md={12}>
					<Field
						component={CustomInput}
						name="businessStartDate"
						label="Business Start Date"
						helperLabel="(Approximate)"
						validate={validateBusinessStartDate}
						normalize={onlyDate}
						placeholder="mm/dd/yyyy"
						tabIndex={2}
					/>
				</Col>
			</Row>
			<hr className={styles.divider} />
			<div>
				<h1 className={styles.header}>
					Processing Method (Must Equal 100%)
				</h1>
				<Row>
					<Col lg={6} md={12}>
						<Field
							component={CustomInput}
							name="physicalCardPresent"
							label="Physical Card Present"
							helperLabel="at Time of Sale (%)"
							validate={validatePhysicalCardPresent}
							normalizeOnBlur={normalizeFloat}
							normalize={onlyPercent}
							placeholder="0.00"
							tabIndex={4}
						/>
						<Field
							component={CustomInput}
							name="monthlyCCV"
							label="Monthly Credit Card Volume"
							validate={validateMonthlyCreditCardVolume}
							normalizeOnBlur={normalizeFloat}
							normalize={(positive, noSpace)}
							placeholder="0.00"
							tabIndex={6}
						/>
						<Field
							component={CustomInput}
							name="highSingleSaleAmount"
							label="Highest Single Sale Amount"
							validate={validateHighestSingleSaleAmount}
							normalizeOnBlur={normalizeFloat}
							normalize={(positive, noSpace)}
							placeholder="0.00"
							tabIndex={8}
						/>
					</Col>
					<Col lg={6} md={12}>
						<Field
							component={CustomInput}
							name="physicalCardNotPresent"
							label="Physical Card Not Present"
							helperLabel="at Time of Sale (%)"
							validate={validatePhysicalCardNotPresent}
							normalizeOnBlur={normalizeFloat}
							normalize={onlyPercent}
							placeholder="0.00"
							tabIndex={5}
						/>
						<Field
							component={CustomInput}
							name="avgSaleAmount"
							label="Average Sale Amount"
							validate={validateAvgSaleAmount}
							normalizeOnBlur={normalizeFloat}
							normalize={(positive, noSpace)}
							placeholder="0.00"
							tabIndex={7}
						/>
					</Col>
				</Row>
			</div>
			<StepButtons
				nextStep={nextStep}
				prevStep={prevStep}
				initialTabIndex={9}
			/>
		</form>
	);
};

const serviceStep = reduxForm({
	form: FORM_NAME,
	destroyOnUnmount: false,
})(ServiceStep);
export { serviceStep as ServiceStep };
